import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { adminApi } from './redux/adminApiSlice';
import { clearAuthCookies } from './utils/cookie';
import Navbar from './navigation/Navbar';
import Dashboard from './pages/Dashboard';
import LocationDashboard from './pages/Locations/LocationsPage';
import Users from './pages/Users';
import Posts from './pages/PostsPage';
import MockPosts from './components/MockPosts/MockPostsPage';
import MockUsers from './pages/MockUsers';
import Settings from './pages/Settings';
import Login from './pages/LoginPage';

import APIHealth from './pages/APIHealth';
import NotificationsPage from './pages/NotificationsPage';
import ReportsPage from './pages/ReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import EnrichmentPage from './pages/EnrichmentPage';
import InterestCopyPage from './pages/InterestCopyPage';
import PlaceClaimsPage from './pages/PlaceClaimsPage';
import ProfilePage from './pages/ProfilePage';
import Security from './pages/Security';
import CountryBoundaries from './pages/CountryBoundaries';

import GridIconExport from './pages/GridIconExport';
import NeighborhoodImporter from './pages/NeighborhoodImporter';
import ImageCalibrator from './pages/ImageCalibrator';
import PulseSimulatorPage from './pages/PulseSimulatorPage';
import QuickPlaceImport from './pages/QuickPlaceImport/QuickPlaceImport';


const store = configureStore({
  reducer: {
    [adminApi.reducerPath]: adminApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(adminApi.middleware),
});

// Create a QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected route wrapper component
// Uses HttpOnly cookies - tokens are automatically sent with requests
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  // Validate authentication by making an API call
  // HttpOnly cookies are automatically sent with credentials: true
  useEffect(() => {
    const validateAuth = async () => {
      try {
        const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true' 
          ? process.env.REACT_APP_REMOTE_API_URL 
          : process.env.REACT_APP_LOCAL_API_URL;
        
        const response = await fetch(`${apiBaseUrl}/api/auth/validate`, {
          method: 'GET',
          credentials: 'include', // Required to send HttpOnly cookies
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Try to refresh token if validation fails
          const refreshResponse = await fetch(`${apiBaseUrl}/api/auth/refresh-token`, {
            method: 'POST',
            credentials: 'include', // Required to send HttpOnly cookies
            headers: {
              'Content-Type': 'application/json',
            },
            // No body needed - refresh token is in HttpOnly cookie
          });
          
          if (refreshResponse.ok) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            // Clear any old localStorage tokens (cleanup)
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('lastTokenTime');
            clearAuthCookies();
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Auth validation error:', error);
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    
    validateAuth();
  }, [navigate]);
  
  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }
  
  // Redirect to login if not authenticated
  if (isAuthenticated === false) {
    return null; // Navigation handled in useEffect
  }

  // Render children if authenticated
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const [useRemoteApi, setUseRemoteApi] = useState(
    localStorage.getItem('useRemoteApi') === 'true'
  );

  useEffect(() => {
    // Update environment variable and localStorage when toggle changes
    localStorage.setItem('useRemoteApi', useRemoteApi.toString());
    // We'll need to reload the app to apply the changes
  }, [useRemoteApi]);

  const toggleApiEndpoint = () => {
    const newValue = !useRemoteApi;
    setUseRemoteApi(newValue);
    
    // Force reload to apply the new API URL
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Find current API URL
  const apiUrl = useRemoteApi 
    ? process.env.REACT_APP_REMOTE_API_URL 
    : process.env.REACT_APP_LOCAL_API_URL;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <Dashboard />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/locations"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <LocationDashboard />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/posts"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <Posts />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/mock-posts"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <MockPosts />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/mock-users"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <MockUsers />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <Users />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <NotificationsPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <Settings 
                    useRemoteApi={useRemoteApi}
                    toggleApiEndpoint={toggleApiEndpoint}
                    apiUrl={apiUrl}
                  />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto'
                }}>
                  <ProfilePage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/api-health"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <APIHealth />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <ReportsPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <AnalyticsPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/enrichment"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <EnrichmentPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/interest-copy"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <InterestCopyPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/place-claims"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <PlaceClaimsPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/security"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto',
                  width: '100%',
                  maxWidth: '100vw'
                }}>
                  <Security />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/grid-icon-export"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <GridIconExport />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/quick-place-import"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto'
                }}>
                  <QuickPlaceImport />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/neighborhood-importer"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <NeighborhoodImporter />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/quick-place-import"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto'
                }}>
                  <QuickPlaceImport />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pulse-simulator"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <PulseSimulatorPage />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/image-calibrator"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px'
                }}>
                  <ImageCalibrator />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/country-boundaries"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <Box component="main" sx={{ 
                  flexGrow: 1,
                  height: 'calc(100vh - 64px)',
                  marginTop: '64px',
                  overflow: 'auto'
                }}>
                  <CountryBoundaries />
                </Box>
              </>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <AppContent />
        </Router>
      </Provider>
    </QueryClientProvider>
  );
};

export default App; 